import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { type ForwardedRef, forwardRef, useState } from 'react';

import { Flex } from '../../flex';
import type { ChipSelectionProps } from '../ChipSelection';
import type { OptionSelectionProps } from '../types';

type ChipMultipleSelectionProps = ChipSelectionProps & {
  value?: OptionSelectionProps['value'][];
  defaultValue?: OptionSelectionProps['value'][];
  onChange?: (value: OptionSelectionProps['value'][]) => void;
};

const ChipMultipleSelection = forwardRef(
  (props: ChipMultipleSelectionProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      value,
      defaultValue,
      options,
      onChange,
      onBlur,
      label,
      helperText,
      row: hasRow,
      disabled: isDisabled,
      ...restProps
    } = props;

    const [selectedValues, setSelectedValues] = useState(value ?? defaultValue ?? []);

    const handleChange = (value: OptionSelectionProps['value']) => {
      setSelectedValues((prev) => {
        const isSelected = prev.includes(value);

        let updatedValues;
        if (isSelected) updatedValues = prev.filter((i) => i !== value);
        else updatedValues = [...prev, value];

        onChange?.(updatedValues);
        onBlur?.();
        return updatedValues;
      });
    };

    return (
      <FormControl {...restProps} ref={ref} fullWidth={!hasRow}>
        {label ? <InputLabel sx={{ position: 'relative', mb: 2 }}>{label}</InputLabel> : null}
        <Flex
          // TODO: Chips align center by parent element
          sx={{
            justifyContent: 'center',
            flexDirection: hasRow ? 'row' : 'column',
            flexWrap: hasRow ? 'wrap' : 'nowrap',
            gap: 2,
          }}
        >
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <Chip
                key={`${option.value}`}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : 'default'}
                size='small'
                label={option.label}
                onClick={() => handleChange(option.value)}
                disabled={isDisabled}
                sx={{
                  height: 50,
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  px: 4,
                  ':hover': { fontSize: 14, fontWeight: 500 },
                  '&.MuiChip-outlined': { px: 3.875 },
                }}
              />
            );
          })}
        </Flex>
        {helperText ? <FormHelperText sx={{ mt: 2 }}>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  },
);

ChipMultipleSelection.displayName = 'ChipMultipleSelection';

export { ChipMultipleSelection };
export type { ChipMultipleSelectionProps };
